import { useMemo } from 'react'
import { format } from 'date-fns'
import { LineChartGraph } from '@/common/components/LineChart'
import { Skeleton } from '@/common/ui/skeleton'

import type { GetActiveUsersResponse } from '@/modules/dashboard/type'

type ActiveUsersGraphProps = {
	data?: GetActiveUsersResponse
	comparisonData?: GetActiveUsersResponse
	enableComparison?: boolean
	isLoading?: boolean
}

export default function ActiveUsersGraph({
	data,
	comparisonData,
	enableComparison = false,
	isLoading = false,
}: ActiveUsersGraphProps) {
	const dataKeys = useMemo(() => {
		const keys: Record<
			string,
			{ label: string; color: string; strokeDasharray?: string }
		> = {
			activeUsers: {
				label: enableComparison ? 'ช่วงปัจจุบัน' : 'ผู้ใช้งานออนไลน์',
				color: enableComparison
					? 'hsl(262.1 83.3% 57.8%)'
					: 'hsl(var(--chart-1))',
			},
		}

		if (enableComparison && comparisonData?.data) {
			keys.comparisonUsers = {
				label: 'ช่วงเปรียบเทียบ',
				color: 'hsl(24.6 95% 53.1%)',
				strokeDasharray: '5 5',
			}
		}

		return keys
	}, [enableComparison, comparisonData])

	const graphData = useMemo(() => {
		if (!data?.data?.length) return []

		return data.data.map((item, index) => ({
			week: format(new Date(item.timestamp), 'HH:mm'),
			activeUsers: item.activeCount,
			...(enableComparison &&
				comparisonData?.data?.[index] && {
					comparisonUsers: comparisonData.data[index].activeCount,
				}),
		}))
	}, [data, comparisonData, enableComparison])

	const description = useMemo(() => {
		if (!data?.startDate || !data?.endDate) return 'ทุก 30 นาที'

		const start = format(new Date(data.startDate), 'dd/MM/yyyy')
		const end = format(new Date(data.endDate), 'dd/MM/yyyy')

		if (
			enableComparison &&
			comparisonData?.startDate &&
			comparisonData?.endDate
		) {
			const compStart = format(new Date(comparisonData.startDate), 'dd/MM/yyyy')
			const compEnd = format(new Date(comparisonData.endDate), 'dd/MM/yyyy')
			return `ช่วงเวลา ${start} - ${end} vs ช่วงเวลาเปรียบเทียบ ${compStart} - ${compEnd}`
		}

		return `ช่วงเวลา ${start} - ${end}`
	}, [data, comparisonData, enableComparison])

	if (isLoading) {
		return (
			<div className="rounded-2xl border bg-card p-6">
				<Skeleton className="mb-4 h-8 w-64" />
				<Skeleton className="mb-6 h-4 w-96" />
				<Skeleton className="h-[400px] w-full" />
			</div>
		)
	}

	if (!graphData.length) {
		return (
			<div className="rounded-2xl border bg-card p-6">
				<h3 className="mb-2 text-xl font-bold">จำนวนผู้ใช้งานออนไลน์</h3>
				<p className="mb-6 text-sm text-muted-foreground">{description}</p>
				<div className="flex h-[400px] items-center justify-center rounded-xl border-2 border-dashed">
					<p className="text-muted-foreground">ไม่มีข้อมูล</p>
				</div>
			</div>
		)
	}

	return (
		<div className="rounded-2xl border bg-card shadow-sm">
			<LineChartGraph
				data={graphData}
				title="จำนวนผู้ใช้งานออนไลน์"
				description={description}
				enableSliding={graphData.length > 100}
				rangeOptions={
					graphData.length > 100
						? [
								{ label: '50', days: 50 },
								{ label: '100', days: 100 },
								{ label: '200', days: 200 },
								{ label: 'All', days: null },
							]
						: []
				}
				dataKeys={dataKeys}
				yAxisFormatter={(value) => Math.round(value).toLocaleString('th-TH')}
				tooltipFormatter={(value) =>
					`${Math.round(Number(value)).toLocaleString('th-TH')} คน`
				}
				tooltipLabelFormatter={(label) => `เวลา: ${label}`}
			/>
		</div>
	)
}
